import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, updateDoc, deleteDoc, where, orderBy, deleteField } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { User } from '../../types/user';
import { Organization } from '../../types/organization';
import toast from 'react-hot-toast';
import Modal from '../common/Modal';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import LoadingScreen from '../common/LoadingScreen';
import { Switch } from '../common/Switch';
import UserPermissionsManager from './UserPermissionsManager';
import { 
  UserIcon, 
  BuildingOfficeIcon, 
  PencilIcon, 
  TrashIcon,
  EyeIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface SystemUser {
  id: string;
  email: string;
  displayName: string;
  photoURL: string;
  role: 'super_admin' | 'admin' | 'user' | 'customer';
  organizationId?: string;
  organizationName?: string;
  isActive?: boolean;
  createdAt?: any; // Firebase timestamp
  lastLogin?: any; // Firebase timestamp
  permissions?: any;
  customerId?: string;
}

const SystemUserManager: React.FC = () => {
  const [users, setUsers] = useState<SystemUser[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [organizationFilter, setOrganizationFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<SystemUser | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch organizations
      const orgsQuery = query(collection(db, 'organizations'));
      const orgsSnapshot = await getDocs(orgsQuery);
      const orgsData = orgsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Organization[];
      setOrganizations(orgsData);

      // Fetch users
      const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
      const usersSnapshot = await getDocs(usersQuery);
      const usersData = usersSnapshot.docs.map(doc => {
        const userData = { id: doc.id, ...doc.data() } as SystemUser;
        
        // Add organization name if user has organizationId
        if (userData.organizationId) {
          const org = orgsData.find(o => o.id === userData.organizationId);
          userData.organizationName = org?.name || 'Ismeretlen szervezet';
        }
        
        return userData;
      });
      
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Hiba történt az adatok betöltése során');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { role: newRole });
      
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { ...user, role: newRole as 'super_admin' | 'admin' | 'user' | 'customer' } : user
        )
      );
      
      toast.success('Felhasználó jogosultság frissítve');
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Hiba történt a jogosultság frissítése során');
    }
  };

  const handleUserStatusChange = async (userId: string, isActive: boolean) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { isActive });
      
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { ...user, isActive } : user
        )
      );
      
      toast.success(`Felhasználó ${isActive ? 'aktiválva' : 'letiltva'}`);
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error('Hiba történt a felhasználó státuszának frissítése során');
    }
  };

  const handleOrganizationChange = async (userId: string, organizationId: string) => {
    try {
      const userRef = doc(db, 'users', userId);
      const organization = organizations.find(org => org.id === organizationId);
      
      // Üres választás = szervezet elvétele: a Firestore undefined-ot nem fogad el, a mezőt törölni kell
      await updateDoc(userRef, { 
        organizationId: organizationId ? organizationId : deleteField(),
        updatedAt: new Date()
      });
      
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { 
            ...user, 
            organizationId: organizationId || undefined,
            organizationName: organization?.name || undefined
          } : user
        )
      );
      
      toast.success(`Felhasználó szervezethez rendelve: ${organization?.name || 'Nincs szervezet'}`);
    } catch (error) {
      console.error('Error updating user organization:', error);
      toast.error('Hiba történt a szervezet hozzárendelése során');
    }
  };


  const [kpi, setKpi] = useState<'all' | 'active' | 'inactive' | 'super_admin' | 'admin' | 'user' | 'customer'>('all');
  const [userToDelete, setUserToDelete] = useState<SystemUser | null>(null);
  const [showPermissions, setShowPermissions] = useState(false);

  const ROLE_META: Record<string, { label: string; chip: string }> = {
    super_admin: { label: 'Super admin', chip: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300' },
    admin: { label: 'Admin', chip: 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300' },
    user: { label: 'Felhasználó', chip: 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-200' },
    customer: { label: 'Ügyfél', chip: 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' }
  };
  const tsToDate = (v: any): Date | null => { if (!v) return null; const d = v?.toDate ? v.toDate() : v?.seconds ? new Date(v.seconds * 1000) : new Date(v); return isNaN(d.getTime()) ? null : d; };
  const fmt = (v: any) => { const d = tsToDate(v); return d ? d.toLocaleString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '–'; };
  const initials = (u: SystemUser) => (u.displayName || u.email || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(x => x[0]?.toUpperCase()).join('') || '?';
  const Avatar: React.FC<{ u: SystemUser; size?: 'sm' | 'lg' }> = ({ u, size = 'sm' }) => (
    u.photoURL
      ? <img src={u.photoURL} alt="" className={`${size === 'lg' ? 'w-12 h-12' : 'w-8 h-8'} rounded-full object-cover flex-shrink-0`} />
      : <span className={`${size === 'lg' ? 'w-12 h-12 text-sm' : 'w-8 h-8 text-[11px]'} rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-semibold flex items-center justify-center flex-shrink-0`}>{initials(u)}</span>
  );

  const filteredUsers = users.filter(user => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = !term || user.displayName?.toLowerCase().includes(term) || user.email?.toLowerCase().includes(term) || user.organizationName?.toLowerCase().includes(term);
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesOrg = organizationFilter === 'all' || user.organizationId === organizationFilter;
    const matchesStatus = statusFilter === 'all' || (statusFilter === 'active' && user.isActive !== false) || (statusFilter === 'inactive' && user.isActive === false);
    const matchesKpi = kpi === 'all' ? true : kpi === 'active' ? user.isActive !== false : kpi === 'inactive' ? user.isActive === false : user.role === kpi;
    return matchesSearch && matchesRole && matchesOrg && matchesStatus && matchesKpi;
  }).sort((a, b) => (tsToDate(b.lastLogin)?.getTime() || 0) - (tsToDate(a.lastLogin)?.getTime() || 0));

  const userStats = {
    total: users.length,
    active: users.filter(u => u.isActive !== false).length,
    inactive: users.filter(u => u.isActive === false).length,
    superAdmins: users.filter(u => u.role === 'super_admin').length,
    admins: users.filter(u => u.role === 'admin').length,
    users: users.filter(u => u.role === 'user').length,
    customers: users.filter(u => u.role === 'customer').length
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await deleteDoc(doc(db, 'users', userToDelete.id));
      setUsers(prev => prev.filter(u => u.id !== userToDelete.id));
      if (selectedUser?.id === userToDelete.id) setSelectedUser(null);
      toast.success('Felhasználó törölve');
    } catch (e) { console.error(e); toast.error('Hiba történt a felhasználó törlése során'); }
    finally { setUserToDelete(null); }
  };

  if (loading) return <LoadingScreen />;

  const tiles: { key: typeof kpi; label: string; n: number; dot?: string }[] = [
    { key: 'all', label: 'Összes', n: userStats.total },
    { key: 'active', label: 'Aktív', n: userStats.active, dot: 'bg-emerald-500' },
    { key: 'inactive', label: 'Inaktív', n: userStats.inactive, dot: 'bg-rose-500' },
    { key: 'super_admin', label: 'Super admin', n: userStats.superAdmins, dot: 'bg-rose-400' },
    { key: 'admin', label: 'Admin', n: userStats.admins, dot: 'bg-violet-500' },
    { key: 'user', label: 'Felhasználó', n: userStats.users, dot: 'bg-gray-400' },
    { key: 'customer', label: 'Ügyfél', n: userStats.customers, dot: 'bg-sky-500' }
  ];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Felhasználók</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">Minden regisztrált felhasználó, szervezettől függetlenül. Szerep, státusz és szervezet helyben állítható.</p>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {tiles.map(tile => (
          <button key={tile.key} onClick={() => setKpi(tile.key)} className={`crm-card px-3 py-2 text-left transition-colors ${kpi === tile.key ? 'border-gray-900 dark:border-white' : 'hover:border-gray-300 dark:hover:border-gray-600'}`}>
            <div className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400 min-w-0">{tile.dot && <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${tile.dot}`} />}<span className="truncate">{tile.label}</span></div>
            <div className="text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-50 leading-tight">{tile.n}</div>
          </button>
        ))}
      </div>

      <div className="crm-card p-3 sm:p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Keresés név, e-mail vagy szervezet alapján…" className="w-full h-9 pl-9 pr-3 text-sm border rounded-lg" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <select value={organizationFilter} onChange={(e) => setOrganizationFilter(e.target.value)} className="h-9 pl-3 pr-8 text-sm border rounded-lg max-w-[220px]">
              <option value="all">Minden szervezet</option>
              <option value="">Szervezet nélkül</option>
              {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div className="md:ml-auto text-xs text-gray-500 dark:text-gray-400 tabular-nums">{filteredUsers.length} / {users.length} felhasználó</div>
        </div>
      </div>

      <div className="crm-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="text-left px-4 py-2.5">Felhasználó</th>
                <th className="text-left px-3 py-2.5">Szerep</th>
                <th className="text-left px-3 py-2.5">Szervezet</th>
                <th className="text-left px-3 py-2.5">Státusz</th>
                <th className="text-left px-3 py-2.5 whitespace-nowrap">Utolsó belépés</th>
                <th className="text-right px-3 py-2.5">Műveletek</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-500 dark:text-gray-400">Nincs a szűrésnek megfelelő felhasználó</td></tr>
              ) : filteredUsers.map(u => {
                const rm = ROLE_META[u.role] || ROLE_META.user;
                return (
                  <tr key={u.id} onDoubleClick={() => setSelectedUser(u)} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                    <td className="px-4 py-2.5">
                      <button type="button" onClick={() => setSelectedUser(u)} className="flex items-center gap-2.5 text-left min-w-0">
                        <Avatar u={u} />
                        <span className="min-w-0"><span className="block text-sm font-medium text-gray-900 dark:text-gray-50 truncate hover:underline">{u.displayName || '(nincs név)'}</span><span className="block text-xs text-gray-500 dark:text-gray-400 truncate">{u.email}</span></span>
                      </button>
                    </td>
                    <td className="px-3 py-2.5">
                      <select value={u.role} onChange={(e) => handleRoleChange(u.id, e.target.value)} className={`h-7 pl-2 pr-7 rounded-full border-0 text-xs font-medium ${rm.chip}`} style={{ backgroundImage: 'none' }}>
                        {Object.entries(ROLE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2.5">
                      <select value={u.organizationId || ''} onChange={(e) => handleOrganizationChange(u.id, e.target.value)} className="h-8 pl-2 pr-7 text-xs border rounded-lg max-w-[200px]">
                        <option value="">– nincs –</option>
                        {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2.5">
                      <label className="inline-flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                        <Switch checked={u.isActive !== false} onChange={(v) => handleUserStatusChange(u.id, v)} size="sm" />{u.isActive !== false ? 'Aktív' : 'Inaktív'}
                      </label>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 tabular-nums whitespace-nowrap">{u.lastLogin ? fmt(u.lastLogin) : <span className="text-gray-400">soha</span>}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-0.5 text-gray-500 dark:text-gray-400">
                        <button type="button" onClick={() => setSelectedUser(u)} className="p-1.5 rounded-md hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white" title="Részletek"><EyeIcon className="w-4 h-4" /></button>
                        <button type="button" onClick={() => setUserToDelete(u)} className="p-1.5 rounded-md hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-900/20" title="Törlés"><TrashIcon className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Felhasználó lapja */}
      {selectedUser && (
        <Modal isOpen onClose={() => { setSelectedUser(null); setShowPermissions(false); }} title={selectedUser.displayName || selectedUser.email} size="3xl" noPadding>
          <div className="flex flex-col min-h-full">
            <div className="px-4 sm:px-6 pt-4 pb-3 border-b border-[var(--crm-border)] flex items-center gap-3">
              <Avatar u={selectedUser} size="lg" />
              <div className="min-w-0 flex-1">
                <div className="text-base font-semibold text-gray-900 dark:text-gray-50 truncate">{selectedUser.displayName || '(nincs név)'}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span>{selectedUser.email}</span>
                  <span className={`inline-flex items-center px-2 h-5 rounded-full text-[11px] font-medium ${(ROLE_META[selectedUser.role] || ROLE_META.user).chip}`}>{(ROLE_META[selectedUser.role] || ROLE_META.user).label}</span>
                  <span className={`inline-flex items-center gap-1 ${selectedUser.isActive !== false ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}><span className={`w-1.5 h-1.5 rounded-full ${selectedUser.isActive !== false ? 'bg-emerald-500' : 'bg-rose-500'}`} />{selectedUser.isActive !== false ? 'Aktív' : 'Inaktív'}</span>
                </div>
              </div>
            </div>
            <div className="flex-1 p-4 sm:p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <section className="crm-card p-4 space-y-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Fiók</h3>
                  <label className="block text-xs text-gray-600 dark:text-gray-300">Szerep
                    <select value={selectedUser.role} onChange={(e) => { handleRoleChange(selectedUser.id, e.target.value); setSelectedUser({ ...selectedUser, role: e.target.value as any }); }} className="mt-1 w-full h-9 pl-3 pr-8 text-sm border rounded-lg">
                      {Object.entries(ROLE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </label>
                  <label className="block text-xs text-gray-600 dark:text-gray-300">Aktív szervezet
                    <select value={selectedUser.organizationId || ''} onChange={(e) => { handleOrganizationChange(selectedUser.id, e.target.value); setSelectedUser({ ...selectedUser, organizationId: e.target.value, organizationName: organizations.find(o => o.id === e.target.value)?.name }); }} className="mt-1 w-full h-9 pl-3 pr-8 text-sm border rounded-lg">
                      <option value="">– nincs –</option>
                      {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                  </label>
                  <div className="flex items-center justify-between text-sm pt-1">
                    <div><div className="font-medium">Fiók aktív</div><div className="text-xs text-gray-500 dark:text-gray-400">Inaktív fiókkal nem lehet belépni.</div></div>
                    <Switch checked={selectedUser.isActive !== false} onChange={(v) => { handleUserStatusChange(selectedUser.id, v); setSelectedUser({ ...selectedUser, isActive: v }); }} size="sm" />
                  </div>
                </section>
                <section className="crm-card p-4 space-y-2 text-sm">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Aktivitás</h3>
                  <div className="flex justify-between gap-2"><span className="text-gray-500">Regisztrált</span><span className="tabular-nums">{fmt(selectedUser.createdAt)}</span></div>
                  <div className="flex justify-between gap-2"><span className="text-gray-500">Utolsó belépés</span><span className="tabular-nums">{selectedUser.lastLogin ? fmt(selectedUser.lastLogin) : 'soha'}</span></div>
                  <div className="flex justify-between gap-2"><span className="text-gray-500">Szervezet</span><span className="truncate">{selectedUser.organizationName || '–'}</span></div>
                  {selectedUser.customerId && <div className="flex justify-between gap-2"><span className="text-gray-500">Ügyfél-fiók</span><span className="font-mono text-xs">{selectedUser.customerId}</span></div>}
                  <div className="flex justify-between gap-2"><span className="text-gray-500">Azonosító</span><span className="font-mono text-xs">{selectedUser.id}</span></div>
                </section>
              </div>

              <section className="crm-card p-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Modul-jogosultságok</h3>
                  <button type="button" onClick={() => setShowPermissions(s => !s)} className="text-xs text-primary-600 hover:underline">{showPermissions ? 'Elrejtés' : 'Szerkesztés'}</button>
                </div>
                {showPermissions ? <div className="mt-3"><UserPermissionsManager userId={selectedUser.id} /></div> : <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Modulonként állítható, hogy a felhasználó lássa / kezelje az adott területet.</p>}
              </section>

              <div className="rounded-xl border border-rose-200 dark:border-rose-800 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="text-sm flex-1"><div className="font-medium text-rose-700 dark:text-rose-300">Felhasználó törlése</div><div className="text-xs text-gray-500 dark:text-gray-400">A Firestore-profil törlődik; a bejelentkezési fiók (Auth) megmarad, amíg az Auth-ban is nem törlöd.</div></div>
                <button type="button" onClick={() => setUserToDelete(selectedUser)} className="crm-btn crm-btn-danger">Törlés…</button>
              </div>
            </div>
            <div className="mt-auto sticky bottom-0 z-10 border-t border-[var(--crm-border)] bg-[var(--crm-surface)] px-4 sm:px-6 py-3 flex justify-end">
              <button type="button" onClick={() => { setSelectedUser(null); setShowPermissions(false); }} className="crm-btn crm-btn-ghost">Bezárás</button>
            </div>
          </div>
        </Modal>
      )}

      <ConfirmDeleteModal
        isOpen={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        onConfirm={confirmDelete}
        title="Felhasználó törlése"
        deleteMessage={`Biztosan törlöd: ${userToDelete?.displayName || ''} (${userToDelete?.email || ''})?`}
        deleteWarning="A felhasználó profilja és jogosultságai törlődnek. Ez nem visszavonható."
      />
    </div>
  );
};

export default SystemUserManager;
